/**
 * THE single source of a transaction-type label in the import feature.
 *
 * Every preview and every rule chip shows the SAME thing for the SAME canonical
 * attribute key: the localized `transactions.attributes.<key>.name` catalog
 * entry — the very catalog the Transactions table renders. Before this existed,
 * the admin mapping-editor preview coloured and labelled rows from a hardcoded,
 * accent-less Spanish map (`Operacion`, `Deposito`, no `staking`) while the
 * customer preview resolved the catalog, so the two disagreed on the same file
 * and a valid `declare_as: staking` rule looked unrecognised to its own author.
 *
 * Keys on the wire are ALWAYS canonical attribute keys (`trade`, `deposit`,
 * `staking`, …) or the `__unknown__` sentinel — never display strings.
 *
 * ⚠️⚠️ THE JSON CATALOG ONLY COVERS THE SEED KEYS. Measured in the browser on
 * 2026-09-22: an attribute created through `/admin/system/attributes` with the
 * denomination «Intereses pagados» came back as the bare slug
 * `loop-interest-expense` in this preview, while `/admin/system/attribute-mappings`
 * printed «Intereses pagados (loop-interest-expense)» and the customer's
 * Transactions table printed «Intereses pagados». Same attribute, same session,
 * three surfaces, two answers — and this is the surface that decides whether the
 * admin publishes the mapping.
 *
 * The sibling resolver (`admin/features/attribute-mappings/utils/target-attribute-label.ts`)
 * already closed the same hole in #331 by reading the attribute's DENOMINATIONS.
 * Here the label arrives pre-resolved: the backend's
 * `TransactionAttributesService.combineWithTranslations` already applies
 * denomination → i18n → key for the requested locale and serves the result as
 * `name` on `/api/transaction-attributes`. Passing that `name` in is therefore
 * NOT a second resolution chain — it is the SAME one, read rather than
 * re-implemented. `useImportAttributeLabel` is the hook that supplies it.
 */

import type { TFunction } from '@platform/i18n/hooks/useTranslation'

/**
 * The V2 engine's sentinel for a row it could not classify. Surfaced VERBATIM by
 * both preview endpoints so the UI can say "we did not recognise this" instead
 * of silently blanking the row.
 */
export const UNKNOWN_ATTRIBUTE_KEY = '__unknown__'

/** True when the key is the unclassified sentinel (or simply absent). */
export function isUnclassifiedAttributeKey(attributeKey: string): boolean {
  return attributeKey === UNKNOWN_ATTRIBUTE_KEY || attributeKey.trim() === ''
}

/**
 * Localize a canonical attribute key for display.
 *
 * @param attributeKey canonical key from the backend (`trade`, `staking`, …)
 * @param t a `useTranslation('imports')` translator — the unclassified label
 *          lives in the `imports` namespace, the catalog in `transactions`
 * @param catalogName the attribute's localized `name` from
 *          `/api/transaction-attributes` (see `useImportAttributeLabel`).
 *          Optional so the pure function stays callable without the catalog;
 *          omitting it reproduces the pre-#2466 behaviour exactly.
 * @returns the localized name; the raw key only when NEITHER the catalog nor
 *          the JSON knows it, never a missing-key placeholder
 */
export function resolveTransactionAttributeLabel(
  attributeKey: string,
  t: TFunction,
  catalogName?: string | null,
): string {
  if (isUnclassifiedAttributeKey(attributeKey)) {
    return t('forms.csv.preview.unclassifiedType')
  }

  // Step 1 — the catalog's own localized name. It is the ONLY thing a
  // runtime-created attribute has, and for a seed attribute it resolves to the
  // very same string the JSON holds, so this never changes an existing label.
  // Guarded against a catalog that echoes the key (older rows with no
  // denomination) so the JSON still gets its turn below.
  const fromCatalog = catalogName?.trim() ?? ''
  if (fromCatalog !== '' && fromCatalog !== attributeKey) {
    return fromCatalog
  }

  // Step 2 — the legacy JSON catalog, which covers the seed keys.
  // Step 3 — the raw key (never a missing-key placeholder).
  return t(`attributes.${attributeKey}.name`, { ns: 'transactions', defaultValue: attributeKey })
}
